import { useCallback, useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Plus, Pencil, Trash2, Phone, Mail, MapPin } from "lucide-react";
import { toast } from "sonner";
import { saas } from "@/lib/api";
import { PageHeader, kr } from "@/components/Layout";
import { FormDialog } from "@/components/FormDialog";

export const CUSTOMER_STATUS = [
  { value: "ny_lead", label: "Ny lead" }, { value: "kontaktet", label: "Kontaktet" },
  { value: "oppfolging", label: "Oppfølging" }, { value: "befaring", label: "Befaring" },
  { value: "tilbud_sendt", label: "Tilbud sendt" }, { value: "vunnet", label: "Vunnet" }, { value: "tapt", label: "Tapt" },
];
const label = (v) => CUSTOMER_STATUS.find((s) => s.value === v)?.label || v;
const EMPTY = { name: "", contact_person: "", phone: "", email: "", address: "", service_type: "", price: 0, notes: "", status: "ny_lead", next_followup: "" };
const FIELDS = [
  { k: "name", label: "Navn / bedrift", full: true }, { k: "contact_person", label: "Kontaktperson" },
  { k: "phone", label: "Telefon" }, { k: "email", label: "E-post" }, { k: "address", label: "Adresse" },
  { k: "service_type", label: "Tjeneste" }, { k: "price", label: "Pris (eks. mva)", type: "number" },
  { k: "status", label: "Status", type: "select", options: CUSTOMER_STATUS },
  { k: "next_followup", label: "Neste oppfølging", type: "date" }, { k: "notes", label: "Notater", type: "textarea" },
];

export default function Customers() {
  const [list, setList] = useState([]);
  const [status, setStatus] = useState("alle");
  const [q, setQ] = useState("");
  const [edit, setEdit] = useState(null);

  const load = useCallback(() => saas.customers.list({ status, q }).then(setList), [status, q]);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!edit.name?.trim()) return toast.error("Navn mangler");
    const body = { ...edit, next_followup: edit.next_followup || null, price: Number(edit.price) || 0 };
    edit.id ? await saas.customers.update(edit.id, body) : await saas.customers.create(body);
    toast.success("Kunde lagret");
    setEdit(null);
    load();
  };
  const remove = async (c) => {
    if (!window.confirm(`Slette ${c.name}?`)) return;
    await saas.customers.remove(c.id);
    load();
  };

  return (
    <div className="p-4 sm:p-8" data-testid="customers-page">
      <PageHeader title="Kunder" subtitle={`${list.length} kunder`}>
        <Input data-testid="customer-search-input" placeholder="Søk..." value={q} onChange={(e) => setQ(e.target.value)} className="w-48 bg-card" />
        <Button data-testid="add-customer-button" onClick={() => setEdit({ ...EMPTY })} className="gap-1.5"><Plus className="h-4 w-4" /> Ny kunde</Button>
      </PageHeader>
      <div className="flex flex-wrap gap-1.5 mb-4">
        {[{ value: "alle", label: "Alle" }, ...CUSTOMER_STATUS].map((s) => (
          <button key={s.value} data-testid={`customer-status-${s.value}`} onClick={() => setStatus(s.value)}
            className={`rounded-full px-3 py-1 text-xs font-semibold border ${status === s.value ? "bg-primary text-primary-foreground border-primary" : "bg-card border-border text-muted-foreground"}`}>
            {s.label}
          </button>
        ))}
      </div>
      {list.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-border bg-card py-16 text-center text-muted-foreground">Ingen kunder enda. Legg til en, eller godkjenn et prospekt og trykk «Legg til som kunde».</div>
      ) : (
        <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-3">
          {list.map((c) => (
            <div key={c.id} data-testid={`customer-card-${c.id}`} className="rounded-2xl border border-border bg-card p-4">
              <div className="flex items-start gap-2">
                <div className="flex-1 min-w-0">
                  <div className="font-heading font-bold truncate">{c.name}</div>
                  <div className="text-xs text-muted-foreground">{c.contact_person}</div>
                </div>
                <span className="text-[11px] font-bold uppercase rounded-full bg-secondary px-2 py-0.5">{label(c.status)}</span>
              </div>
              <div className="mt-3 space-y-1 text-xs text-muted-foreground">
                {c.phone && <div className="flex gap-1.5"><Phone className="h-3.5 w-3.5" />{c.phone}</div>}
                {c.email && <div className="flex gap-1.5 break-all"><Mail className="h-3.5 w-3.5 shrink-0" />{c.email}</div>}
                {c.address && <div className="flex gap-1.5"><MapPin className="h-3.5 w-3.5" />{c.address}</div>}
              </div>
              <div className="mt-3 flex items-center gap-2 text-xs">
                {c.price > 0 && <span className="font-bold">{kr(c.price)}</span>}
                {c.next_followup && <span className="text-[#b45309] font-semibold">Følg opp {c.next_followup}</span>}
                <div className="ml-auto flex gap-1">
                  <Button size="icon" variant="ghost" className="h-8 w-8" data-testid={`edit-customer-${c.id}`} onClick={() => setEdit({ ...EMPTY, ...c, next_followup: c.next_followup || "" })}><Pencil className="h-4 w-4" /></Button>
                  <Button size="icon" variant="ghost" className="h-8 w-8 text-destructive" data-testid={`delete-customer-${c.id}`} onClick={() => remove(c)}><Trash2 className="h-4 w-4" /></Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
      {edit && (
        <FormDialog open onOpenChange={() => setEdit(null)} title={edit.id ? "Rediger kunde" : "Ny kunde"} testid="customer-dialog"
          fields={FIELDS} value={edit} onChange={setEdit} onSubmit={save} />
      )}
    </div>
  );
}
